import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { LoadingSpinner } from "@mapmycustomers/ui";

import CardType from "@app/enum/dashboard/CardType";
import PreviewType from "@app/enum/dashboard/PreviewType";
import BaseCard from "@app/scene/dashboard/components/cards/common/BaseCard";
import Count from "@app/scene/dashboard/components/cards/common/Count";
import Menu from "@app/scene/dashboard/components/cards/common/Menu";
import {
  fetchOpenStageDealAmountCardData,
  hideOpenStageDealAmountDrillDown,
  showOpenStageDealAmountDrillDown,
} from "@app/scene/dashboard/store/actions";
import OpenStageDealAmountCardData from "@app/scene/dashboard/store/cards/openStageDealAmount/OpenStageDealAmountCardData";
import { getOpenStageDealAmountDrillDownConfiguration } from "@app/scene/dashboard/store/cards/openStageDealAmount/selectors";
import CardContentProps from "@app/scene/dashboard/types/CardContentProps";
import { RootState } from "@app/store/rootReducer";
import OpenStageDealAmountCardConfiguration from "@app/types/dashboard/cards/openStageDealAmount/OpenStageDealAmountCardConfiguration";
import OpenStageDealAmountViewState from "@app/types/dashboard/cards/openStageDealAmount/OpenStageDealAmountViewState";

import GoalInformation from "../../common/GoalInformation";
import messages from "../messages";

import styles from "./CardContentComponent.module.scss";
import DrillDown from "./DrillDown";
import Subtitle from "./Subtitle";

const SAMPLE_DATA: OpenStageDealAmountCardData = {
  amount: 15000,
};

const SAMPLE_CONFIGURATION: OpenStageDealAmountCardConfiguration = {
  criteria: {
    dealStageIds: [],
    rottingOptions: [],
  },
  dateRange: {
    days: 90,
  },
  goal: {
    enabled: true,
    target: 10,
  },
  preferences: null,
};

interface Props
  extends CardContentProps<OpenStageDealAmountCardConfiguration, OpenStageDealAmountViewState> {
  drillDownVisible: boolean;
  onFetchData?: typeof fetchOpenStageDealAmountCardData;
  onHideDrillDown: typeof hideOpenStageDealAmountDrillDown;
  onShowDrillDown: typeof showOpenStageDealAmountDrillDown.request;
}

const CardContentComponent: React.FC<Props> = ({
  configuration,
  draggable,
  drillDownVisible,
  fullWidth,
  onChangeView,
  onCopy,
  onEdit,
  onFetchData,
  onHideDrillDown,
  onRemove,
  onShowDrillDown,
  preview,
  scope,
}) => {
  const intl = useIntl();

  const [data, setData] = useState<OpenStageDealAmountCardData | undefined>(
    preview === PreviewType.SAMPLE_DATA ? SAMPLE_DATA : undefined
  );

  const finalConfiguration =
    preview === PreviewType.SAMPLE_DATA ? SAMPLE_CONFIGURATION : configuration;

  useEffect(
    () => {
      if (preview !== PreviewType.SAMPLE_DATA) {
        onFetchData?.({
          callback: setData,
          configuration: finalConfiguration,
          scope,
        });
      }
    },
    // Removing scope from deps because scope never changes for a card on a board.
    // When scope changes, that is a new card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalConfiguration, onFetchData, preview]
  );

  const handleViewData = useCallback(
    () => onShowDrillDown?.({ configuration: finalConfiguration, scope }),
    [finalConfiguration, onShowDrillDown, scope]
  );

  const handleChangeFullWidth = useCallback(
    (fullWidth: boolean) => {
      onChangeView?.({ fullWidth });
    },
    [onChangeView]
  );

  return (
    <BaseCard
      contentClassName={styles.container}
      draggable={draggable}
      menu={
        <Menu
          expanded={fullWidth}
          onCopy={onCopy}
          onEdit={onEdit}
          onRemove={onRemove}
          onResize={preview !== undefined ? undefined : handleChangeFullWidth}
          onViewData={preview !== undefined ? undefined : handleViewData}
        />
      }
      subtitle={<Subtitle scope={scope} />}
      title={
        finalConfiguration.customTitle?.trim() ||
        intl.formatMessage(messages.cardTitle, {
          date: (text: ReactNode) => <>{text}</>,
          days: finalConfiguration.dateRange.days,
          neverContacted: finalConfiguration.dateRange.days === null,
        })
      }
    >
      {!data ? (
        <LoadingSpinner />
      ) : (
        <>
          <Count className={styles.count} count={data.amount} currency />
          {!!finalConfiguration.goal?.enabled && (
            <GoalInformation
              currency
              goalConfiguration={finalConfiguration.goal}
              scope={scope}
              type={CardType.OPEN_STAGE_DEAL_AMOUNT}
              value={data.amount}
            />
          )}
        </>
      )}
      {drillDownVisible && (
        <DrillDown
          configuration={configuration}
          onEdit={onEdit}
          onHide={onHideDrillDown}
          scope={scope}
        />
      )}
    </BaseCard>
  );
};

const mapStateToProps = (
  state: RootState,
  props: CardContentProps<OpenStageDealAmountCardConfiguration, OpenStageDealAmountViewState>
) => ({
  drillDownVisible:
    getOpenStageDealAmountDrillDownConfiguration(state) ===
    (props.temporaryConfiguration ?? props.configuration),
});

const mapDispatchToProps = {
  onFetchData: fetchOpenStageDealAmountCardData,
  onHideDrillDown: hideOpenStageDealAmountDrillDown,
  onShowDrillDown: showOpenStageDealAmountDrillDown.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CardContentComponent);
